import { createFileRoute, Link } from "@tanstack/react-router";
import { QaPage, qaJsonLd } from "@/components/QaPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { IdCheckDrill } from "@/components/IdCheckDrill";
import { LAST_REVIEWED } from "@/lib/official-sources";

const QUESTION = "Vilken åldersgräns gäller för servering av alkohol?";
const FAQS = [
  { q: "Vilken åldersgräns gäller för servering av alkohol?", a: "18 år. Gästen måste ha fyllt 18 för att bli serverad alkohol på restaurang, bar eller café (3 kap. 7 § alkohollagen)." },
  { q: "Varför är åldersgränsen 20 år på Systembolaget?", a: "Detaljhandel med starköl, vin och sprit kräver 20 år enligt 3 kap. 7 § första stycket. Servering och folköl i butik har en lägre gräns på 18 år." },
  { q: "Hur gammal måste den som serverar alkohol vara?", a: "Den som lämnar ut alkoholdrycker ska ha fyllt 20 år (8 kap. 12 § alkohollagen). Serveringsansvarig person ska också vara minst 20 år. Övrig personal som inte hanterar alkohol kan vara yngre." },
  { q: "Måste jag alltid begära legitimation?", a: "Nej, men du har bevisbördan för att gästen är över 18. Praxis och Folkhälsomyndighetens vägledning är att begära id av alla som ser ut att vara under 25 år." },
  { q: "Vad händer om vi serverar en underårig gäst?", a: "Kommunen utfärdar normalt varning vid första överträdelsen och kan återkalla tillståndet vid upprepning (9 kap. 17–18 §§). Langning till underårig kan även ge böter eller fängelse." },
];

const SITUATIONS = [
  {
    h: "Ett sällskap där bara några kan legitimera sig",
    p: "Servera inte till bordet som helhet. Varje gäst som ska dricka alkohol måste kunna styrkas vara över 18 år. Kan en i sällskapet inte legitimera sig får den personen inte serveras, och du ska förhindra att drycken förs vidare till hen (3 kap. 7 § och 3 kap. 9 § alkohollagen).",
  },
  {
    h: "En vuxen beställer åt någon som ser ung ut",
    p: "Detta är den klassiska langningssituationen. Om du misstänker att drycken är avsedd för en underårig ska serveringen nekas, även om beställaren är över 20 år. Att lämna ut alkohol som går vidare till underårig kan bedömas som olovlig dryckeshantering enligt 11 kap. 7 §.",
  },
  {
    h: "Utgången eller sliten id-handling",
    p: "En utgången legitimation styrker inte åldern. Godtagbara handlingar är svenskt id-kort, pass, körkort, nationellt id-kort och främlingspass. Är kortet skadat, saknar hologram eller inte går att jämföra med gästen ska servering nekas.",
  },
  {
    h: "Digitalt id i mobilen",
    p: "Digital legitimation i app accepteras inte som fysisk id-handling vid ålderskontroll i servering. Begär en fysisk handling – annars vilar bevisbördan kvar på dig som tillståndshavare.",
  },
  {
    h: "Uteservering och stora ytor",
    p: "Ansvaret gäller hela det tillståndsgivna serveringsområdet. Personalen måste kunna överblicka uteserveringen så att drycker inte förs över till underåriga gäster vid närliggande bord.",
  },
  {
    h: "Slutet sällskap med ungdomar, till exempel studentfest",
    p: "Serveringstillstånd får inte ges för lokal där huvuddelen av gästerna är under 18 år (8 kap. 2 §). Vid blandade sällskap krävs id-kontroll vid varje utlämning, inte bara vid entrén.",
  },
  {
    h: "Personal som lämnar ut drycken",
    p: "Den som lämnar ut alkoholdrycker ska ha fyllt 20 år (8 kap. 12 §). En 18-åring i baren får därför inte servera alkohol, även om gästen är kontrollerad.",
  },
];

const SHORT_ANSWER =  "Åldersgränsen för servering av alkohol är 18 år – gästen måste ha fyllt 18 för att bli serverad öl, vin eller sprit på restaurang, bar eller café. Samma 18-årsgräns gäller folköl i butik, medan köp på Systembolaget kräver 20 år, liksom den personal som lämnar ut alkoholdrycker (3 kap. 7 § och 8 kap. 12 § alkohollagen 2010:1622). Begär alltid legitimation av gäster som ser ut att vara under 25 år – langning till någon under 18 är straffbart och servering till underårig ger normalt varning eller återkallat tillstånd.";

export const Route = createFileRoute("/fraga/vilken-aldersgrans-galler-for-servering")({
  component: () => (
    <QaPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Frågor & svar", to: "/fraga" },
        { label: "Vilken åldersgräns gäller för servering av alkohol" },
      ]}
      question={QUESTION}
      shortAnswer={SHORT_ANSWER}
      topic="åldersgränser och ID-kontroll"
      table={{
        caption: "Åldersgränser för alkohol i Sverige – snabb översikt",
        headers: ["Var", "Åldersgräns", "Vad gäller"],
        rows: [
          ["Restaurang, bar, café (servering)", "18 år", "Gästen måste ha fyllt 18 för att bli serverad alkohol"],
          ["Folköl i livsmedelsbutik", "18 år", "Samma gräns som servering – max 3,5 volymprocent"],
          ["Systembolaget", "20 år", "Gäller all detaljhandel av starköl, vin och sprit"],
          ["ID-kontroll", "Under 25 år", "Begär legitimation av alla som ser ut att vara under 25"],
          ["Langning", "Förbjudet", "Att köpa ut till underårig är straffbart (11 kap. 7 §)"],
        ],
      }}
      body={"Rättslig grund: 3 kap. 7 § första stycket alkohollagen – \"Alkoholdrycker eller alkoholdrycksliknande preparat får inte säljas eller annars lämnas ut till den som inte har fyllt 20 år. Beträffande folköl och servering av alkoholdrycker eller alkoholdrycksliknande preparat gäller motsvarande den som inte har fyllt 18 år.\"\n\nFolkölsförsäljning i butik följer alltså samma 18-årsgräns som servering, medan Systembolaget och partihandel kräver 20 år.\n\nLangningsförbudet: 3 kap. 9 § – det är förbjudet att i annat fall än som avses i 3 kap. 7 § lämna alkoholdryck som gåva eller lån till någon som inte fyllt 20 år (18 år för folköl/servering). Köp eller försök till köp till underårig bestraffas som olovlig dryckeshantering enligt 11 kap. 7 § (böter eller fängelse högst två år, vid grovt brott högst fyra år).\n\nServeringsansvar: Tillståndshavaren har bevisbördan för att gästen fyllt 18 (prop. 2009/10:125 s. 102). Vid tveksamhet ska legitimation begäras – Folkhälsomyndighetens vägledning rekommenderar kontroll av alla under 25 år. Servering till underårig är typiskt sett grund för varning vid första överträdelsen och återkallelse vid upprepning (9 kap. 17–18 §§).\n\nUngdomslokal: Enligt 8 kap. 2 § får serveringstillstånd inte beviljas för lokal där huvuddelen av gästerna är under 18 år."}
      source={{ label: "Alkohollagen (2010:1622) 3 kap. 7 § – Riksdagen", href: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/alkohollag-20101622_sfs-2010-1622/" }}
      extraSources={[{ label: "Folkhälsomyndigheten – Ålderskontroll vid servering", href: "https://www.folkhalsomyndigheten.se/livsvillkor-levnadsvanor/alkohol-narkotika-dopning-tobak-och-spel-andts/tillsynsvagledning-enligt-alkohollagen/serveringstillstand/" }, { label: "Prop. 2009/10:125 – En ny alkohollag", href: "https://www.regeringen.se/rattsliga-dokument/proposition/2010/03/prop.-200910125/" }]}
      deepGuide={{ to: "/alkohollagen-kapitel-3", label: "Alkohollagen kap. 3 – ålderskontroll" }}
      related={[{"to": "/fraga/far-jag-servera-till-onykter-gast", "label": "Får jag servera alkohol till en onykter gäst?"}, {"to": "/fraga/hur-langt-far-jag-ha-oppet", "label": "Hur länge får jag ha öppet med serveringstillstånd?"}, {"to": "/fraga/ar-id-kontroll-obligatorisk", "label": "Är ID-kontroll obligatorisk vid alkoholservering?"}]}
      lastReviewed={LAST_REVIEWED}
    >
      <div className="mt-8 rounded-2xl border border-accent/30 bg-accent/5 p-5">
        <p className="font-display text-lg font-semibold">Ska du skriva provet för serveringstillstånd?</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Åldersgränserna är en av de vanligaste frågorna på kommunens prov. Testa 5 gratisfrågor direkt, eller läs mer
          om <Link to="/kunskapsprov-alkohollagen" className="underline">alkoholtillstånd prov</Link>.
        </p>
        <Link
          to="/gratis-ovningsfragor-serveringstillstand"
          className="mt-4 inline-flex items-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
        >
          Kunskapsprov alkoholtillstånd frågor – testa gratis
        </Link>
      </div>
      <IdCheckDrill />
      <section className="mt-10">
        <h2 className="font-display text-2xl font-semibold">Svåra serveringssituationer – så bedöms de</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Åldersgränsen är enkel på pappret, men bevisbördan ligger på tillståndshavaren (3 kap. 7 § alkohollagen).
          Så här hanteras de situationer som oftast leder till anmärkning vid tillsyn.
        </p>
        <div className="mt-4 space-y-3">
          {SITUATIONS.map((s) => (
            <div key={s.h} className="rounded-xl border border-border bg-card p-5">
              <p className="font-medium">{s.h}</p>
              <p className="mt-2 text-sm text-muted-foreground">{s.p}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="mt-10">
        <h2 className="font-display text-2xl font-semibold">Vanliga följdfrågor om åldersgränsen</h2>
        <div className="mt-4 space-y-3">
          {FAQS.map((f) => (
            <details key={f.q} className="group rounded-xl border border-border bg-card p-5">
              <summary className="cursor-pointer list-none flex justify-between gap-4 font-medium">
                <span>{f.q}</span>
                <span className="text-accent transition-transform group-open:rotate-45 text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </QaPage>
  ),
  head: () => ({
    meta: [
      { title: "Åldersgräns för alkohol på restaurang – 18 eller 20 år?" },
      { name: "description", content: "Du måste vara 18 år för att bli serverad alkohol på restaurang och 20 år för att handla på Systembolaget. Se åldersgränserna och läs om ID-kontroll." },
      { property: "og:title", content: "Åldersgräns för alkohol på restaurang – 18 eller 20 år?" },
      { property: "og:description", content: "Du måste vara 18 år för att bli serverad alkohol på restaurang och 20 år för att handla på Systembolaget. Se åldersgränserna och läs om ID-kontroll." },


      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://alkoholprovet.se/fraga/vilken-aldersgrans-galler-for-servering" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Åldersgräns för alkohol på restaurang – 18 eller 20 år?" },
      { name: "twitter:description", content: "Du måste vara 18 år för att bli serverad alkohol på restaurang och 20 år för att handla på Systembolaget. Se åldersgränserna och läs om ID-kontroll." },

    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/fraga/vilken-aldersgrans-galler-for-servering" }],
    scripts: [
      qaJsonLd({
        url: "https://alkoholprovet.se/fraga/vilken-aldersgrans-galler-for-servering",
        question: QUESTION,
        answer: SHORT_ANSWER,
      }),
      faqJsonLd(FAQS),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Frågor & svar", url: "https://alkoholprovet.se/fraga" },
        { label: "Vilken åldersgräns gäller för servering av alkohol", url: "https://alkoholprovet.se/fraga/vilken-aldersgrans-galler-for-servering" },
      ]),
    ],
  }),
});
